import AddRemoveImageButton from "@/components/AddRemoveImageButton";
import { FlexColumn, FlexRowAlignCenter } from "@/components/Flex";
import HtmlText from "@/components/HtmlText";
import WithScrollTrigger from "@/components/WithScrollTrigger";
import ProgressPill, { MasteryPill } from "@/features/StudyPages/components/ProgressPill";
import SortingPill from "@/features/StudyPages/components/SortingPill";
import br from "@/styles/breakpoints.module.css";
import { themeColors } from "@/utils/themeColors";
import {
    Flashcard,
    FlashcardSide,
    FlashcardSortingEnum,
    StudySessionProgressEnum,
} from "@knowt/syncing/graphql/schema";
import { Flex } from "@/components/Flex";
import clsx from "clsx";
import { isEqual } from "lodash-es";
import dynamic from "next/dynamic";
import { memo } from "react";
import WithClickFlashcardAudio from "./components/WithClickFlashcardAudio";
import Circular from "@/components/Circular";
import { FlashcardsMastery } from "@knowt/syncing/hooks/study/statsUtils";

const FlashcardControls = dynamic(() => import("./components/FlashcardControls"), { ssr: false });

const getMediaMode = () => {
    if (typeof window !== "undefined") {
        const path = window.location.pathname;
        return path.includes("media");
    } else {
        return false;
    }
};

const PreviewFlashcardCard = ({
    index,
    flashcard,
    progress,
    sort,
    readOnly,
    style,
    flashcardMastery,
    onMasteryDetailsClick,
}: {
    index: number;
    flashcard: Flashcard;
    progress?: StudySessionProgressEnum;
    sort?: FlashcardSortingEnum;
    readOnly: boolean;
    style?: React.CSSProperties;
    flashcardMastery?: FlashcardsMastery["flashcardId"];
    onMasteryDetailsClick?: () => void;
}) => {
    const { term, definition, image, secondaryImage } = flashcard;

    // for media page
    const isMediaMode = getMediaMode();
    const hasImages = image || secondaryImage;

    const renderTopItems = () => (
        <FlexRowAlignCenter
            className={clsx(br.mdDownRow, br.mdDownJustifySpaceBetween, br.mdDownWidth100)}
            style={{ gap: "1rem", marginBottom: "1.5rem" }}>
            <Circular
                radius="4rem"
                className="secondaryTextBold1"
                style={{ marginRight: "auto", backgroundColor: themeColors.background, borderRadius: 9 }}>
                {index + 1}
            </Circular>

            {/* NOTE: since `FlashcardControls` is dynamic imported, we put fixed height to prevent layout shift */}
            <Flex style={{ height: "4rem" }}>
                <WithScrollTrigger threshold={50} disableHysteresis>
                    <FlashcardControls readOnly={readOnly} flashcard={flashcard} audioSide={FlashcardSide.BOTH} />
                </WithScrollTrigger>
            </Flex>
            <SortingPill sort={sort} />
            {flashcardMastery ? (
                <MasteryPill mastery={flashcardMastery} onClick={onMasteryDetailsClick} />
            ) : (
                <ProgressPill progress={progress} />
            )}
        </FlexRowAlignCenter>
    );

    return (
        <FlexColumn
            className={br.smDownColumn}
            style={{
                padding: "2.5rem 2rem",
                borderRadius: "2rem",
                backgroundColor: isMediaMode ? themeColors.background : themeColors.neutralWhite,
                ...style,
            }}>
            {renderTopItems()}

            {hasImages ? (
                <FlexRowAlignCenter
                    className={clsx(br.mdDownColumn, br.mdDownAlignStart)}
                    style={{ flex: 1, gap: "2em", wordBreak: "break-word" }}>
                    {/* TERM WITH IMAGE */}
                    <FlexRowAlignCenter
                        style={{ flex: 2, gap: "inherit", overflowWrap: "break-word", alignItems: "center" }}>
                        {!!secondaryImage && (
                            <Flex
                                className={clsx({
                                    [br.lgDownDisplayNone]: !secondaryImage,
                                })}>
                                <AddRemoveImageButton
                                    editable={false}
                                    alt={term || "term image"}
                                    image={secondaryImage}
                                    size="9rem"
                                    style={{ visibility: secondaryImage ? "visible" : "hidden" }}
                                />
                            </Flex>
                        )}
                        <WithClickFlashcardAudio flashcard={flashcard} side={FlashcardSide.TERM}>
                            <HtmlText text={term} />
                        </WithClickFlashcardAudio>
                    </FlexRowAlignCenter>

                    {/* DEFINITION WITH IMAGE */}
                    <FlexRowAlignCenter
                        className={br.mdDownRowReverse}
                        style={{ flex: 3, gap: "inherit", overflowWrap: "break-word" }}>
                        <WithClickFlashcardAudio flashcard={flashcard} side={FlashcardSide.DEFINITION}>
                            <HtmlText text={definition} />
                        </WithClickFlashcardAudio>
                        {!!image && (
                            <Flex className={clsx({ [br.lgDownDisplayNone]: !image })}>
                                <AddRemoveImageButton
                                    editable={false}
                                    alt={definition ?? "definition image"}
                                    image={image}
                                    size="9rem"
                                    style={{ visibility: image ? "visible" : "hidden" }}
                                />
                            </Flex>
                        )}
                    </FlexRowAlignCenter>
                </FlexRowAlignCenter>
            ) : (
                <Flex className={clsx(br.mdDownColumn)} style={{ flex: 1, gap: "2em", wordBreak: "break-word" }}>
                    {/* TERM WITHOUT IMAGE */}
                    <Flex style={{ flex: 2, overflowWrap: "break-word" }}>
                        <WithClickFlashcardAudio flashcard={flashcard} side={FlashcardSide.TERM}>
                            <HtmlText text={term} />
                        </WithClickFlashcardAudio>
                    </Flex>

                    {/* DEFINITION WITHOUT IMAGE */}
                    <Flex style={{ flex: 3, overflowWrap: "break-word" }}>
                        <WithClickFlashcardAudio flashcard={flashcard} side={FlashcardSide.DEFINITION}>
                            <HtmlText text={definition} />
                        </WithClickFlashcardAudio>
                    </Flex>
                </Flex>
            )}
        </FlexColumn>
    );
};

export default memo(PreviewFlashcardCard, (prevProps, nextProps) => isEqual(prevProps, nextProps));
